import { Glue42 } from "../../glue";
import { default as CallbackRegistryFactory, CallbackRegistry } from "callback-registry";
import { SharedContextSubscriber } from "./contexts";
import { addOrRemoveChannel, getWindowsWithChannels, switchChannel } from "./agm";
import shortid from "shortid";
export class ChannelsImpl implements Glue42.Channels.API {
    private subsKey = "subs";
    private changedKey = "changed";
    private isInitialJoin = true;

    private registry: CallbackRegistry = CallbackRegistryFactory();
    private currentContext: string;
    private lastUpdate: { context: Glue42.Channels.ChannelContext, updaterId?: string };
    private pendingReplays: any = {};

    constructor(private shared: SharedContextSubscriber, private interop: Glue42.Interop.API, private getWindows: () => Glue42.Windows.API, private logger: Glue42.Logger.API) {
        this.shared.subscribe(this.handler.bind(this));

        if (typeof window !== "undefined" && typeof window.glue42gd !== "undefined") {
            this.currentContext = (window.glue42gd as any).initialChannel;
            if (this.currentContext) {
                this.joinNoSelectorSwitch(this.currentContext);
            }
        }
    }

    public subscribe(callback: (data: object, context: Glue42.Channels.ChannelContext, updaterId: string) => void): () => void {
        if (typeof callback !== "function") {
            throw new Error("Please provide the callback as a function!");
        }

        // We want to replay the current context for late joiners
        // Some caveats:
        // 1. We need to consider that unsub might be called before we replay the initial set - in which case we should not do the replay - we wrap unsubscribe
        // and use pendingReplays to handle this well)
        // 2. We need to prevent double context updated calls with the same value. This  might happen if you do subscirbe -> publish fast - we use pendingReplays to prevent double calls.
        // 3. We don't use replay args of callback registry, as they might reorder updates in case of fast updates (e.g. subscribe -> leave might result in [undefined, context] updates,
        // instead of [context, undefined]
        const id = shortid();
        this.pendingReplays[id] = true;
        if (this.lastUpdate) {
            let lastUpdate = Object.assign({}, this.lastUpdate);
            setTimeout(() => {
                if (this.pendingReplays[id]) {
                    if (this.lastUpdate) {
                        lastUpdate = this.lastUpdate;
                    }
                    callback(lastUpdate.context.data, lastUpdate.context, lastUpdate.updaterId);
                }
                delete this.pendingReplays[id];
            }, 0);
        }

        const unsub = this.registry.add(this.subsKey, callback);
        return () => {
            this.pendingReplays[id] = false;
            unsub();
        };
    }

    public async subscribeFor(name: string, callback: (data: object, context: Glue42.Channels.ChannelContext, updaterId: string) => void): Promise<() => void> {
        if (typeof name !== "string") {
            throw new Error("Please provide the name as a string!");
        }
        if (typeof callback !== "function") {
            throw new Error("Please provide the callback as a function!");
        }
        const unsubscribeFunc = await this.shared.subscribeFor(name, callback);
        return unsubscribeFunc;
    }

    public async publish(data: object, name?: string): Promise<void> {
        if (typeof data !== "object") {
            throw new Error("Please provide the data as an object!");
        }

        if (name) {
            if (typeof name !== "string") {
                throw new Error("Please provide the name as a string!");
            }

            if (!this.shared.isChannel(name)) {
                return Promise.reject(new Error(`A channel with name: ${name} doesn't exist!`));
            }

            return this.shared.updateData(name, data);
        }

        if (!this.currentContext) {
            throw new Error("Not joined to any channel!");
        }
        return this.shared.updateData(this.currentContext, data);
    }

    public all(): Promise<string[]> {
        const channelNames = this.shared.all();
        return Promise.resolve(channelNames);
    }

    public async list(): Promise<Glue42.Channels.ChannelContext[]> {
        const channelNames = await this.all();
        const channelContexts = await Promise.all(channelNames.map((channelName) => this.get(channelName)));
        return channelContexts;
    }

    public get(name: string): Promise<Glue42.Channels.ChannelContext> {
        if (typeof name !== "string") {
            return Promise.reject(new Error("Please provide the channel name as a string!"));
        }

        return this.shared.getContextData(name);
    }

    /* Returns info about my channel */
    public getMy(): Promise<Glue42.Channels.ChannelContext> {
        if (!this.currentContext) {
            return Promise.resolve(undefined);
        }
        return this.get(this.currentContext);
    }

    public async join(name: string, windowId?: string): Promise<void> {
        if (windowId) {
            return switchChannel(name, windowId);
        }
        return this.joinCore(name);
    }

    public async joinNoSelectorSwitch(name: string): Promise<void> {
        return this.joinCore(name, false);
    }

    public leave(windowId?: string) {
        if (windowId) {
            return switchChannel(undefined, windowId);
        }
        return this.leaveCore();
    }

    public leaveNoSelectorSwitch() {
        return this.leaveCore(false);
    }

    public current(): string {
        return this.currentContext;
    }

    public my(): string {
        return this.current();
    }

    public changed(callback: (channel: string) => void): () => void {
        if (typeof callback !== "function") {
            throw new Error("Please provide the callback as a function!");
        }

        // don't use replay args as they might fail if changed->leave
        const current = this.current();
        if (current) {
            setTimeout(() => {
                callback(this.current());
            }, 0);
        }

        return this.registry.add(this.changedKey, callback);
    }

    public onChanged(callback: (channel: string) => void): () => void {
        return this.changed(callback);
    }

    public async add(info: Glue42.Channels.ChannelContext): Promise<Glue42.Channels.ChannelContext> {
        if (typeof info !== "object") {
            throw new Error("Please provide the info as an object!");
        }
        if (typeof info.name === "undefined") {
            throw new Error("info.name is missing!");
        }
        if (typeof info.name !== "string") {
            throw new Error("Please provide the info.name as a string!");
        }
        if (typeof info.meta === "undefined") {
            throw new Error("info.meta is missing!");
        }
        if (typeof info.meta !== "object") {
            throw new Error("Please provide the info.meta as an object!");
        }
        if (typeof info.meta.color === "undefined") {
            throw new Error("info.meta.color is missing!");
        }
        if (typeof info.meta.color !== "string") {
            throw new Error("Please provide the info.meta.color as a string!");
        }

        const context: Glue42.Channels.ChannelContext = {
            name: info.name,
            meta: info.meta || {},
            data: info.data || {}
        };

        // Note that we use `update` instead of `add` so that if the channel already exists we don't overwrite it.
        await this.shared.updateChannel(info.name, context);

        await addOrRemoveChannel("addChannel", info.name, info.meta.color);

        return context;
    }

    public async remove(channel: string): Promise<void> {
        if (typeof channel !== "string") {
            throw new Error("Please provide the channel name as a string!");
        }

        await this.shared.remove(channel);

        await addOrRemoveChannel("removeChannel", channel);
    }

    public async getWindowsOnChannel(channel: string): Promise<Glue42.Windows.GDWindow[]> {
        if (typeof channel !== "string") {
            throw new Error("Please provide the channel name as a string!");
        }
        const windowInfos = await this.getWindowsWithChannels({ channels: [channel] });
        return windowInfos.map((w) => w.window);
    }

    public async getWindowsWithChannels(filter: Glue42.Channels.WindowWithChannelFilter): Promise<Glue42.Channels.WindowOnChannelInfo[]> {
        try {
            const info = await getWindowsWithChannels(filter);
            const windows = this.getWindows();
            if (info?.windows) {
                return info.windows.map((windowInfo) => {
                    const window = windows.findById(windowInfo.windowId);
                    return {
                        window,
                        channel: windowInfo.channel,
                        application: windowInfo.application
                    };
                });
            }
        } catch (er) {
            this.logger.error(`Error getting all channel enabled windows. This method is available since Glue42 3.12`, er);
        }
        return [];
    }

    private handler(data: object, context: Glue42.Channels.ChannelContext, updaterId: string): any {
        if (!context && !updaterId) {
            this.lastUpdate = undefined;
            return;
        }

        this.lastUpdate = { context, updaterId };
        this.pendingReplays = {};
        this.registry.execute(this.subsKey, data, context, updaterId);
    }

    private async joinCore(name: string, changeSelector = true) {
        if (typeof name !== "string") {
            throw new Error("Please provide the channel name as a string!");
        }

        if (!this.isInitialJoin && name === this.currentContext) {
            // ignore joining the channel that we're already on, except when this is the very first call
            return;
        }
        this.isInitialJoin = false;

        // Checks if a channel exists with the provided name.
        const doesChannelExist = (channelName: string): boolean => {
            const channelNames = this.shared.all();
            return channelNames.includes(channelName);
        };

        // Wait for 3k ms for the channel to appear if it doesn't already exist.
        if (!doesChannelExist(name)) {
            const channelExistsPromise = new Promise<void>((resolve, reject) => {
                // Check every 100 ms for a channel with the provided name.
                const intervalId = setInterval(() => {
                    if (doesChannelExist(name)) {
                        clearTimeout(timeoutId);
                        clearInterval(intervalId);
                        resolve();
                    }
                }, 100);

                const timeoutId = setTimeout(() => {
                    clearInterval(intervalId);

                    return reject(new Error(`A channel with name: ${name} doesn't exist!`));
                }, 3000);
            });

            await channelExistsPromise;
        }

        this.currentContext = name;
        this.lastUpdate = undefined;

        await this.shared.switchChannel(name);
        if (changeSelector) {
            switchChannel(name);
        }
        this.registry.execute(this.changedKey, name);
    }

    private leaveCore(changeSelector = true): Promise<void> {
        this.currentContext = undefined;
        this.registry.execute(this.changedKey, undefined);
        this.shared.leave();
        if (changeSelector) {
            switchChannel();
        }
        return Promise.resolve();
    }
}
